import useAssignBooksToSubjectGroups from "api/BookResource/useAssignBooksToSubjectGroups";
import useFetchSubjectGroups from "api/SubjectGroupResource/useFetchSubjectGroups";
import { useFormik } from "formik";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { useState, useEffect } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { BsPencil, BsSearch, BsCheckCircle, BsPeople } from "react-icons/bs";
import {
	FiArrowRight,
	FiX,
	FiUsers,
	FiCheckCircle,
	FiAlertCircle,
	FiLayers,
	FiArrowLeft,
} from "react-icons/fi";
import StepIndicator from "components/StepIndicator";

const AssignSubjectGroups = ({ books, className, children }) => {
	const [display, setDisplay] = useState(false);
	const [currentStep, setCurrentStep] = useState(0);

	const steps = [
		{ label: "Select Groups", icon: <FiLayers /> },
		{ label: "Confirm Assignment", icon: <FiCheckCircle /> },
	];

	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

	// Debounce search term
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
		}, 500);

		return () => clearTimeout(timer);
	}, [searchTerm]);

	const { data: subjectGroupsData, isLoading: isLoadingSubjectGroups } = useFetchSubjectGroups(
		{
			page: 1,
			rows_per_page: 10,
			search: debouncedSearchTerm,
		},
		true,
	);

	const [selectedGroups, setSelectedGroups] = useState([]);

	// Reset selected groups when dialog opens
	useEffect(() => {
		if (display) {
			setSelectedGroups([]);
			setSearchTerm("");
			setCurrentStep(0);
		}
	}, [display]);

	const subjectGroups = subjectGroupsData?.data?.data;

	const tableData = subjectGroups?.map((subjectGroup) => ({
		id: subjectGroup.id,
		name: subjectGroup.name,
		created_by: subjectGroup?.user?.first_name + " " + subjectGroup?.user?.last_name,
		institution_name: subjectGroup?.user?.institutions[0]?.name,
		institution_contactNumber: subjectGroup?.user?.institutions[0]?.contact_number,
		institution_address: subjectGroup?.user?.institutions[0]?.address,
		assigned_users_count: subjectGroup.assigned_users_count,
	}));

	const { mutateAsync, isLoading: isAssigningBooks } = useAssignBooksToSubjectGroups();

	const { values, handleSubmit, setFieldValue } = useFormik({
		initialValues: {
			subjectGroups: [...selectedGroups],
			books: [...books],
		},
		onSubmit: async (data) => {
			setCurrentStep(1);
		},

		enableReinitialize: true,
	});

	const handleConfirmSubmit = async () => {
		const subjectGroups = values.subjectGroups.map((group) => group.id);
		const payload = { subjectGroups, books: values.books.map((book) => book.id) };
		await mutateAsync(payload);
		setCurrentStep(2);
	};

	const totalNumberOfUsersInSelectedGroups = values.subjectGroups?.reduce(
		(accumulator, group) => accumulator + group.assigned_users_count,
		0,
	);

	const notAssignable = (book) => {
		const assignable = Number(book?.available_to_assign ?? book?.number_assignable ?? 0);
		return totalNumberOfUsersInSelectedGroups > assignable || assignable <= 0;
	};

	const getBookAssignmentInfo = (book) => {
		const copiesSubscribed = Number(book?.copies_subscribed ?? 0);
		const subscriptionSeatsAssigned = Number(book?.subscription_seats_assigned ?? 0);
		const availableToAssign = Number(book?.available_to_assign ?? book?.number_assignable ?? 0);

		return {
			copiesSubscribed,
			availableToAssign,
			alreadyAssigned: subscriptionSeatsAssigned,
			hasSubscription: copiesSubscribed > 0,
		};
	};

	const assignableBooksCount = books?.filter((book) => !notAssignable(book))?.length || 0;

	return (
		<>
			<button
				type="button"
				className={`appearance-none border-0 ${className} `}
				onClick={() => setDisplay(true)}
			>
				{children || <BsPencil size={15} />}
			</button>

			<Dialog
				visible={display}
				onHide={() => setDisplay(false)}
				breakpoints={{
					"960px": "90vw",
					"0px": "100vw",
				}}
				className="font-sans"
				style={{ maxWidth: "900px" }}
				showHeader={false}
				contentClassName="rounded-md relative"
				blockScroll={false}
			>
				<button
					type="button"
					onClick={() => setDisplay(false)}
					className="absolute top-4 right-4 hover:bg-gray-100 rounded-full p-2 transition-colors z-10"
				>
					<FiX size={24} />
				</button>

				<div className="pt-8 pb-6">
					{/* Header Section */}
					<div className="px-6 pb-4 border-b border-gray-200">
						<h3 className="font-cera-black text-akgreenest text-2xl mb-2">
							Allocate Books to Subject Groups
						</h3>
						<p className="text-gray-600 text-sm">
							{currentStep === 0 &&
								"Select subject groups below to allocate the selected books to their current students"}
							{currentStep === 1 && "Review your assignment details and confirm"}
							{currentStep === 2 && "Assignment completed successfully"}
						</p>
						<StepIndicator steps={steps} currentStep={currentStep} />
					</div>

					{/* Summary Cards */}
					{currentStep === 0 && (
						<div className="grid grid-cols-4 gap-4 px-6 py-4">
							<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
								<div className="flex items-center gap-2 text-blue-700 mb-1">
									<FiCheckCircle size={18} />
									<span className="text-sm font-medium">Books Selected</span>
								</div>
								<p className="text-2xl font-bold text-blue-900">{books?.length || 0}</p>
							</div>
							<div className="bg-green-50 border border-green-200 rounded-lg p-4">
								<div className="flex items-center gap-2 text-green-700 mb-1">
									<FiCheckCircle size={18} />
									<span className="text-sm font-medium">Books with Seats Available</span>
								</div>
								<p className="text-2xl font-bold text-green-900">{assignableBooksCount}</p>
							</div>
							<div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
								<div className="flex items-center gap-2 text-purple-700 mb-1">
									<FiLayers size={18} />
									<span className="text-sm font-medium">Groups Selected</span>
								</div>
								<p className="text-2xl font-bold text-purple-900">
									{selectedGroups?.length || 0}
								</p>
							</div>
							<div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
								<div className="flex items-center gap-2 text-orange-700 mb-1">
									<FiUsers size={18} />
									<span className="text-sm font-medium">Total Students</span>
								</div>
								<p className="text-2xl font-bold text-orange-900">
									{totalNumberOfUsersInSelectedGroups || 0}
								</p>
							</div>
						</div>
					)}

					{/* Main Content */}
					<div className="px-6 py-4">
						{currentStep === 0 && (
							<form onSubmit={handleSubmit} className="space-y-6">
								{/* Books List */}
								<div className="bg-gray-50 rounded-lg p-4">
									<h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
										<span>📚</span>
										<span>Books to Assign</span>
									</h4>
									<div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
										{books?.map((book) => {
											const info = getBookAssignmentInfo(book);
											return (
												<div
													key={book.id}
													className={`bg-white border rounded-lg p-3 ${
														notAssignable(book)
															? "border-red-300 bg-red-50"
															: "border-green-300"
													}`}
												>
													<div className="flex items-start justify-between mb-2">
														<div className="flex-1 min-w-0">
															<p
																className={`font-medium truncate ${
																	notAssignable(book)
																		? "text-gray-400 line-through"
																		: "text-gray-900"
																}`}
															>
																{book?.title}
															</p>
															<p className="text-sm text-gray-500">
																by {book?.author}
															</p>
														</div>
														<div className="ml-3 flex-shrink-0">
															{notAssignable(book) ? (
																<span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-medium">
																	<FiAlertCircle size={14} />
																	May need more seats
																</span>
															) : (
																<span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
																	<FiCheckCircle size={14} />
																	Available
																</span>
															)}
														</div>
													</div>
													{info.hasSubscription && (
														<div className="flex items-center gap-3 text-xs text-gray-600 mt-1 pt-2 border-t border-gray-200">
															<span className="flex items-center gap-1">
																<span className="font-medium">Seat capacity:</span>
																<span className="text-blue-600 font-semibold">
																	{info.copiesSubscribed}
																</span>
															</span>
															<span className="flex items-center gap-1">
																<span className="font-medium">Allocated:</span>
																<span className="text-orange-600 font-semibold">
																	{info.alreadyAssigned}
																</span>
															</span>
															<span className="flex items-center gap-1">
																<span className="font-medium">
																	Seats remaining:
																</span>
																<span className="text-green-600 font-semibold">
																	{info.availableToAssign}
																</span>
															</span>
														</div>
													)}
												</div>
											);
										})}
									</div>
									{totalNumberOfUsersInSelectedGroups > 0 &&
									books.some((book) => notAssignable(book)) ? (
										<div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
											<div className="flex items-start gap-3">
												<FiAlertCircle size={20} className="text-red-500 mt-0.5" />
												<div className="flex-1">
													<h4 className="font-medium text-red-800">
														Insufficient Seats
													</h4>
													<p className="text-sm text-red-700 mt-1">
														The number of users in the selected subject groups exceeds
														the available seats for one or more books. To assign these
														books, you need to add more seats to your subscription.
													</p>
													<button
														type="button"
														className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
														onClick={() =>
															(window.location.href = "/my-account/subscriptions")
														}
													>
														Add Seats
													</button>
												</div>
											</div>
										</div>
									) : totalNumberOfUsersInSelectedGroups > 0 ? (
										<p className="text-xs text-gray-500 mt-3 italic">
											The assignment will be aborted if the number of users in any
											selected subject group exceeds the available seats for any book.
										</p>
									) : null}
								</div>

								{/* Search Section */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Search and Select Subject Groups
									</label>
									<div className="relative">
										<input
											type="text"
											name="searchTerm"
											placeholder="Type to search subject groups by name..."
											className="w-full h-12 border border-gray-300 rounded-lg pr-4 pl-11 focus:ring-2 focus:ring-akgreener focus:border-transparent"
											value={searchTerm}
											onChange={(e) => setSearchTerm(e.target.value)}
										/>
										<span className="absolute left-4 top-1/2 -translate-y-1/2">
											{isLoadingSubjectGroups ? (
												<AiOutlineLoading3Quarters
													size={18}
													className="text-gray-400 animate-spin"
												/>
											) : (
												<BsSearch size={18} className="text-gray-400" />
											)}
										</span>
									</div>
								</div>

								{/* Subject Groups Table */}
								<div className="border border-gray-200 rounded-lg overflow-hidden">
									{tableData && tableData.length > 0 ? (
										<DataTable
											value={tableData}
											selection={selectedGroups}
											onSelectionChange={(e) => {
												setSelectedGroups(e.value);
												setFieldValue("subjectGroups", [...e.value]);
											}}
											loading={isLoadingSubjectGroups}
											tableClassName="font-sans text-sm"
											className="w-full"
											autoLayout
										>
											<Column
												selectionMode="multiple"
												headerStyle={{ width: "3em" }}
											></Column>
											<Column field="name" header="Subject Group Name" />
											<Column field="created_by" header="Created by" sortable />
											<Column field="institution_name" header="Institution" sortable />
											<Column
												field="assigned_users_count"
												header="Students"
												sortable
												body={(rowData) => (
													<span className="inline-flex items-center gap-1">
														<FiUsers size={14} className="text-gray-500" />
														{rowData.assigned_users_count}
													</span>
												)}
											/>
										</DataTable>
									) : !isLoadingSubjectGroups ? (
										<div className="py-12 text-center text-gray-500">
											<FiLayers size={48} className="mx-auto mb-3 text-gray-300" />
											<p className="font-medium">No subject groups found</p>
											<p className="text-sm">
												{searchTerm
													? "Try a different search term"
													: "Start typing to search for subject groups"}
											</p>
										</div>
									) : null}
								</div>

								{/* Action Buttons */}
								<div className="flex justify-end gap-3 pt-4 border-t">
									<button
										type="button"
										onClick={() => setDisplay(false)}
										className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium"
									>
										Cancel
									</button>
									<button
										type="submit"
										disabled={
											isAssigningBooks ||
											selectedGroups.length === 0 ||
											books.length === 0 ||
											books.some((book) => notAssignable(book))
										}
										className="flex items-center gap-2 px-6 py-3 rounded-lg bg-akgreener text-white hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
									>
										Review Assignment
										<FiArrowRight size={20} />
									</button>
								</div>
							</form>
						)}

						{currentStep === 1 && (
							<div className="space-y-6">
								{/* Confirmation Summary */}
								<div className="bg-green-50 border border-green-200 rounded-lg p-6">
									<div className="flex items-start gap-4">
										<div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
											<FiCheckCircle size={24} className="text-green-600" />
										</div>
										<div className="flex-1">
											<h4 className="font-medium text-green-900 mb-2">
												Ready to Assign
											</h4>
											<p className="text-sm text-green-700 mb-4">
												You are about to assign {books.length} book
												{books.length !== 1 ? "s" : ""} to {selectedGroups.length}{" "}
												subject group{selectedGroups.length !== 1 ? "s" : ""} (
												{totalNumberOfUsersInSelectedGroups} student
												{totalNumberOfUsersInSelectedGroups !== 1 ? "s" : ""}).
											</p>
											<div className="grid grid-cols-2 gap-4 text-sm">
												<div>
													<span className="font-medium text-gray-700">Books:</span>
													<ul className="mt-1 space-y-1">
														{books.map((book) => (
															<li key={book.id} className="text-gray-600">
																• {book.title}
															</li>
														))}
													</ul>
												</div>
												<div>
													<span className="font-medium text-gray-700">
														Subject Groups:
													</span>
													<ul className="mt-1 space-y-1">
														{selectedGroups.map((group) => (
															<li key={group.id} className="text-gray-600">
																• {group.name} ({group.assigned_users_count}{" "}
																students)
															</li>
														))}
													</ul>
												</div>
											</div>
										</div>
									</div>
								</div>

								{/* Action Buttons */}
								<div className="flex justify-end gap-3 pt-4 border-t">
									<button
										type="button"
										onClick={() => setCurrentStep(0)}
										className="flex items-center gap-2 px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium"
									>
										<FiArrowLeft size={20} />
										Back to Selection
									</button>
									<button
										type="button"
										onClick={handleConfirmSubmit}
										disabled={isAssigningBooks}
										className="flex items-center gap-2 px-6 py-3 rounded-lg bg-akgreener text-white hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
									>
										{isAssigningBooks ? (
											<>
												<AiOutlineLoading3Quarters size={20} className="animate-spin" />
												Assigning...
											</>
										) : (
											<>
												Confirm Assignment
												<FiArrowRight size={20} />
											</>
										)}
									</button>
								</div>
							</div>
						)}

						{currentStep === 2 && (
							<div className="text-center space-y-6">
								<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
									<BsCheckCircle size={32} className="text-green-600" />
								</div>
								<div>
									<h4 className="font-medium text-gray-900 mb-2">
										Assignment Successful!
									</h4>
									<p className="text-sm text-gray-600">
										{books.length} book{books.length !== 1 ? "s have" : " has"} been
										successfully assigned to {selectedGroups.length} subject group
										{selectedGroups.length !== 1 ? "s" : ""}.
									</p>
								</div>
								<div className="flex justify-center gap-3 pt-4 border-t">
									<button
										type="button"
										onClick={() => setDisplay(false)}
										className="px-6 py-3 rounded-lg bg-akgreener text-white hover:bg-green-600 transition-colors font-medium"
									>
										Done
									</button>
								</div>
							</div>
						)}
					</div>
				</div>
			</Dialog>
		</>
	);
};

export default AssignSubjectGroups;
